import React, { useMemo, useState } from "react";
import { Product } from "../../product";
import { Checkbox, Dropdown } from "semantic-ui-react";
import { Column } from "./Column";
import { ProductListRenderContext } from "../ObjectTable";
import styled from "styled-components";
import { SelectionBannerAction } from "../selection-banner/ProductListSelectionBanner";

const AlignCheckboxAndDropdown = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
  gap: 10px;
  & > .ui.dropdown > .dropdown.icon {
    margin: 0 !important;
    margin-left: 0.5em !important;
  }
`;
export const ProductListSelectColumn = {
  name: "select",
  collapsing: true,
  visible: true,
  sortable: false,
  renderCell: (
    p: Product,
    column: Column,
    context: ProductListRenderContext
  ): JSX.Element => {
    const { selectedProducts, allSelected, onSelectRowChange } = context;
    return (
      <Checkbox
        className="select"
        checked={allSelected || selectedProducts?.has(p.id)}
        data-product-id={p.id}
        onChange={(e, { checked }): void => onSelectRowChange(checked, p.id)}
      />
    );
  },
  renderHeaderCell: (
    column: Column,
    context: ProductListRenderContext
  ): JSX.Element => {
    const {
      products,
      selectedProducts,
      allSelected,
      onSelectAllOnCurrentPage,
      onObjectSelectionAction,
      totalProducts,
    } = context;
    const productsIds = products.map((product) => product.id);
    const allRowsAreSelected =
      allSelected ||
      productsIds.filter((productId) => selectedProducts.has(productId))
        .length === products.length;

    const [open, setOpen] = useState(false);

    // With an unfetched count (avoid_counting_products) the size comparison says
    // nothing, so only an explicit "select all" tells us everything is selected.
    const allProductsSelected = useMemo(
      () =>
        allSelected ||
        (totalProducts !== null && selectedProducts.size >= totalProducts),
      [allSelected, totalProducts, selectedProducts.size]
    );

    const selectionSummary = (): string => {
      if (allSelected && !selectedProducts.size) return "All items selected";
      if (!selectedProducts.size) {
        return totalProducts === null
          ? "No items selected"
          : `Total selectable items: ${totalProducts}`;
      }
      return totalProducts === null
        ? `Selected ${selectedProducts.size} items`
        : `Selected ${selectedProducts.size} of ${totalProducts} items`;
    };

    return (
      <AlignCheckboxAndDropdown
        onMouseEnter={(): void => setOpen(true)}
        onMouseLeave={(): void => setOpen(false)}
      >
        <Checkbox
          data-testid={"product-list-select-all-active-on-page"}
          className="select-all"
          checked={allRowsAreSelected}
          onChange={(e, { checked }): void => onSelectAllOnCurrentPage(checked)}
        />
        <Dropdown text="Select" open={open}>
          <Dropdown.Menu>
            <Dropdown.Header
              data-testid="product-list-selected-count"
              content={selectionSummary()}
            />
            <Dropdown.Item
              data-testid="product-list-select-all-active-on-page-in-dropdown"
              text={`${allRowsAreSelected ? "Deselect" : "Select"} all ${
                products.length
              } item(s) on this page`}
              onClick={(): void => {
                onSelectAllOnCurrentPage(!allRowsAreSelected);
                setOpen(false);
              }}
            />
            <Dropdown.Item
              data-testid="product-list-select-all-products"
              disabled={allProductsSelected}
              text={allProductsSelected ? "All Selected" : "Select All"}
              onClick={(): void => {
                onObjectSelectionAction(
                  SelectionBannerAction.SELECT_EVERYTHING
                );
                setOpen(false);
              }}
            />
            <Dropdown.Item
              disabled={selectedProducts.size <= 0}
              data-testid="product-list-clear-all-selected-products"
              text="Clear Selection"
              onClick={(): void => {
                onObjectSelectionAction(SelectionBannerAction.CLEAR);
                setOpen(false);
              }}
            />
          </Dropdown.Menu>
        </Dropdown>
      </AlignCheckboxAndDropdown>
    );
  },
};
